import { ProductSpecs } from './specs';
import { convertToProductShapeId, isShapeAttribute, SERVICE_CLIENT_ID } from '../settings';
import { DownloadableTemplate } from './downloadableTemplates';
import { Project } from './project';
import { objectToQuery } from '../utilities/url';
export interface Html {
  __html: string;
}

export interface Product {
  name: string;
  shortName: string;
  mpId: string;
  sku: string;
  displayOrderWeight: number;
  baseQuantity: number;
  upsellPriceHTML: string;
  specs: ProductSpecs;
  specsGrouping: string;
  downloadableTempates: DownloadableTemplate[];
  attributeChoices: AttributeChoice[];
}

interface PossibleValue {
  id: string;
  value: string;
}

export interface AttributeChoice {
  isShapeAttribute: boolean;
  attributeId: string;
  choiceId: string; // per Nathan Longnecker, these happen to be numbers, but we should treat them as opaque string-identifiers
}

interface Attribute {
  key: string;
  displayName: string;
  possibleValues: PossibleValue[];
  tags: string[];
}

export interface ProductShape {
  id: string;
  sku: string;
  attributeChoices: AttributeChoice[];
}

async function getAllAttributesForMP(mpId: string, culture: string): Promise<Attribute[]> {
  const query = {
    requestor: SERVICE_CLIENT_ID,
    locale: culture,
  };
  const response = await fetch(
    `https://merchproduct.storefront.vpsvc.com/api/v1/VariantAttributes/${mpId}/${objectToQuery(
      query,
    )}`,
  );

  if (!response.ok) {
    throw new Error(`Error fetching attributes. ${response.status} - ${response.statusText}`);
  }

  try {
    return await response.json();
  } catch (e) {
    throw new Error(`Error parsing response from VariantAttributes service`);
  }
}

/**
 * Given a set of configurations, return a set of SKUs matching those configurations
 * @param mpId
 * @param culture - e.g. en-us
 * @param configurations - list of lists of choiceIds
 */
async function batchGetSKUsWithConfiguration(
  mpId: string,
  culture: string,
  configurations: AttributeChoice[][],
): Promise<{ [productShapeId: string]: string }> {
  // map our configurations to the expected format e.g. [choiceId1, choiceId2, ...]
  const formattedConfigurations = configurations.map(attributeChoices =>
    attributeChoices.map(attributeChoice => attributeChoice.choiceId),
  );

  // when we send our data to MerPS, we lose the concept of "isShapeAttribute"
  // so we'll "remember" which choices were shape-attribute choices, so that we can re-apply that context to the returned data
  const shapeAttributeChoices = new Set(
    configurations
      .reduce((acc, configuration) => [...acc, ...configuration], []) // merge all arrays into one array
      .filter(attributeChoice => attributeChoice.isShapeAttribute) // remove any non-shape-attributes
      .map(attributeChoice => attributeChoice.choiceId), // map to choiceIds; the Set will take care of de-duplicating
  );

  const query = {
    requestor: SERVICE_CLIENT_ID,
    locale: culture,
    options: formattedConfigurations.map(configuration => configuration.join(',')),
  };
  const response = await fetch(
    `https://merchproduct.storefront.vpsvc.com/api/v2/Navigation/Options/${mpId}/${objectToQuery(
      query,
    )}`,
  );

  if (!response.ok) {
    throw new Error(`Error fetching attributes. ${response.status} - ${response.statusText}`);
  }

  try {
    const responseJSON = await response.json();
    // remove any entries where there are no compatible SKUs, convert the choicesId to a ProductShapeId,
    // then reduce to an object with the shape { [ProductShapeId: string]: SKU }
    return Object.entries(responseJSON.options as { [optionKey: string]: string[] })
      .filter(([choicesKey, compatibleSKUs]) => compatibleSKUs.length !== 0)
      .reduce<{ [choicesKey: string]: string }>((acc, [choicesKey, compatibleSKUs]) => {
        acc[
          convertToProductShapeId(
            // the key that comes back from the api is a comma-delimeted list of choiceIds
            // we'll convert it to an array, and remove any attributes which are not shape-based
            // then convert that to a ProductShapeId
            choicesKey.split(',').filter(choice => shapeAttributeChoices.has(choice)),
          )
        ] = compatibleSKUs[0];
        return acc;
      }, {});
  } catch (e) {
    throw new Error(`Error parsing response from VariantAttributes service`);
  }
}

interface GetAllUniqueProductShapesOptions {
  project?: Project;
}

export async function getAllUniqueProductShapes(
  mpId: string,
  language: string,
  options: GetAllUniqueProductShapesOptions = {},
) {
  const {
    project, // optionally add a project which will be used to filter for compatible results
  } = options;

  // get the choices that are already baked in to the project
  let projectChoices: AttributeChoice[] = [];
  if (
    project &&
    project.quantitiesByVariantAttributeChoices &&
    project.quantitiesByVariantAttributeChoices.length > 0
  ) {
    projectChoices = Object.entries(
      project.quantitiesByVariantAttributeChoices[0].variantAttributeChoices,
    ).reduce<AttributeChoice[]>((acc, [attributeId, choiceId]) => {
      acc.push({
        isShapeAttribute: isShapeAttribute(attributeId),
        attributeId,
        choiceId,
      });

      return acc;
    }, []);
  }

  const allAttributes = await getAllAttributesForMP(mpId, language);

  const shapeAttributes = allAttributes
    .filter(attribute => isShapeAttribute(attribute.key))
    .filter(
      attribute =>
        !projectChoices.some(attributeChoice => attributeChoice.attributeId === attribute.key),
    );

  if (shapeAttributes.length === 0) {
    throw new Error(
      'None of the attributes for the given mpId match the given shape-attribute keys',
    );
  }

  // compute each possible combination of attributes
  const attributeChoiceCombinations = shapeAttributes.reduce<AttributeChoice[][]>(
    (combinations, attribute) => {
      const nextCombinations: AttributeChoice[][] = [];
      attribute.possibleValues.forEach(possibleValue => {
        combinations.forEach(combination => {
          nextCombinations.push([
            ...combination,
            {
              isShapeAttribute: isShapeAttribute(attribute.key),
              attributeId: attribute.key,
              choiceId: possibleValue.id,
            },
          ]);
        });
      });

      return nextCombinations;
    },
    [projectChoices],
  );

  if (attributeChoiceCombinations.length === 0) {
    throw new Error('No combinations could be generated based on the possible values');
  }

  const attributeChoicesToSKUMap = await batchGetSKUsWithConfiguration(
    mpId,
    language,
    attributeChoiceCombinations,
  );

  return Object.entries(attributeChoicesToSKUMap).map<ProductShape>(([productShapeId, sku]) => {
    // find the attributeChoiceCombination that matches the SKU
    const attributeChoiceCombination = attributeChoiceCombinations.find(
      attributeChoiceCombination => {
        const psid = convertToProductShapeId(
          attributeChoiceCombination
            .filter(attributeChoice => isShapeAttribute(attributeChoice.attributeId))
            .map(attributeChoice => attributeChoice.choiceId),
        );
        return psid === productShapeId;
      },
    );

    if (attributeChoiceCombination === undefined) {
      throw new Error('Unable to match SKU to attributeChoiceCombination');
    }

    return {
      id: productShapeId,
      sku,
      attributeChoices: attributeChoiceCombination,
    };
  });
}
